import {globalWindow} from '../../libs/shared/helpers/src/browser/browserHelpers';
import notificationIcon from '../assets/images/notificationIcon.png';

/*
 * Setup.
 */

export enum NotificationPermissionsEnum {
  DENIED = 'denied',
  GRANTED = 'granted',
  DEFAULT = 'default',
}

/** Request permission to display notifications. */
export function setupNotifications() {
  if (!hasNotification()) {
    return undefined;
  }

  const {permission} = Notification;

  // If we've already been denied, nothing to do.
  if (permission === NotificationPermissionsEnum.DENIED) {
    return undefined;
  }

  // If we've already been granted, nothing to do either.
  if (permission === NotificationPermissionsEnum.GRANTED) {
    return undefined;
  }

  // Test the Notification constructor.
  if (!isNewNotificationSupported()) {
    return undefined;
  }

  // Otherwise, request permission.
  return Notification.requestPermission();
}

/*
 * API.
 */

export enum NotificationResultsEnum {
  CLICKED,
  CLOSED,
}

export interface NotificationOptions {
  title: string;
  body?: string;
  iconUrl?: string;
}

export function showNotificationAsync(options: NotificationOptions) {
  // Check if we're allowed to display notifications.
  if (!hasNotification() || Notification.permission !== NotificationPermissionsEnum.GRANTED) {
    return Promise.resolve(NotificationResultsEnum.CLOSED);
  }

  // Display the notification.
  const notification = new Notification(options.title, {
    body: options.body,
    icon: options.iconUrl || String(notificationIcon),
  });

  // Return a promise with the notification result.
  return new Promise<NotificationResultsEnum>((resolve, reject) => {
    notification.onerror = () => reject(new Error('Displaying a notification failed.'));
    notification.onclose = () => resolve(NotificationResultsEnum.CLOSED);
    notification.onclick = () => {
      // Focus the window.
      parent.focus();
      globalWindow.focus();

      // Close the notification.
      notification.close();

      // Notify the caller.
      resolve(NotificationResultsEnum.CLICKED);
    };
  });
}

export function hasNotification() {
  return typeof Notification !== 'undefined' && Notification.requestPermission;
}

/* eslint no-new: 0 */
/** Test the Notification constructor to make sure it works. */
function isNewNotificationSupported() {
  // Make sure the Notification API exists.
  if (!hasNotification()) {
    return false;
  }

  // If permissions were already granted, it's too late to test.
  if (Notification.permission === NotificationPermissionsEnum.GRANTED) {
    throw new Error('This should be called before "requestPermission".');
  }

  // Test the Notification constructor.
  try {
    new Notification('');
  } catch (e) {
    if (e instanceof TypeError) {
      return false;
    }
  }

  return true;
}
